import * as R from 'remeda';
import { useSelector } from 'react-redux';
import { AnnotationAutomationBlocker } from '@rossum/api-client/automationBlockers';
import { IconHelp } from '@rossum/ui/icons/tabler';
import {
  Link,
  List,
  ListItem,
  Stack,
  SvgIcon,
  Typography,
} from '@rossum/ui/material';
import HTMLMessage from '../../../../../components/DatapointMessage/HTMLMessage';
import { isEmbedded } from '../../../../../constants/config';
import { DEVELOPER_HUB_LINK } from '../../../../../constants/values';
import { snakeToSpaces, upperFirst } from '../../../../../lib/keyConvertor';
import { hideArticleLinksSelector } from '../../../../../redux/modules/organization/selectors';
import { MessageDetailLinks } from '../../sidebar-items/shared/MessageDetailLinks';
import { BlockersIcon } from './BlockersIcon';

const getArticleLink = (type: string) => {
  switch (type) {
    case 'suggested_edit_present':
    case 'is_duplicate':
      return `${DEVELOPER_HUB_LINK}/docs/automation-rules-overview`;

    default:
      return '';
  }
};

const getTypeName = (type: AnnotationAutomationBlocker['type']) =>
  upperFirst(snakeToSpaces(type));

type AnnotationBlockerProps = {
  blocker: AnnotationAutomationBlocker;
};

export const AnnotationBlocker = ({ blocker }: AnnotationBlockerProps) => {
  const hideArticleLinks = useSelector(hideArticleLinksSelector);

  const messages =
    // `content` is in `extension` blocker
    blocker.details?.content ||
    // `messageContent` is in `error_message` blocker
    blocker.details?.messageContent ||
    [];

  const messagesWithDetails = messages.map((message, index) => ({
    message,
    detail: blocker.details?.detail?.[index],
  }));

  const uniqueMessages = R.uniqueBy(messagesWithDetails, item => item.message);

  return (
    <Stack direction="row" spacing={1}>
      <BlockersIcon />

      <Stack>
        <Stack direction="row" spacing={1} alignItems="center">
          <Typography variant="body2">{getTypeName(blocker.type)}</Typography>
          {!isEmbedded() &&
            !hideArticleLinks &&
            getArticleLink(blocker.type) && (
              <Link
                href={getArticleLink(blocker.type)}
                target="_blank"
                rel="noopener noreferrer"
              >
                <SvgIcon fontSize="medium" color="info">
                  <IconHelp />
                </SvgIcon>
              </Link>
            )}
        </Stack>
        {uniqueMessages.length > 0 && (
          <List
            disablePadding
            dense
            sx={{
              listStyleType: 'disc',
              color: 'text.secondary',
              pl: 3,
            }}
          >
            {uniqueMessages.map(messageWithDetail => (
              <ListItem
                sx={{
                  display: 'list-item',
                  p: 0,
                }}
                key={messageWithDetail.message}
              >
                <Typography
                  variant="body2"
                  color="secondary"
                  sx={{ overflowX: 'auto' }}
                  component="div"
                >
                  <HTMLMessage content={messageWithDetail.message} />
                </Typography>
                <MessageDetailLinks detail={messageWithDetail.detail ?? null} />
              </ListItem>
            ))}
          </List>
        )}
      </Stack>
    </Stack>
  );
};
